import type { BusinessUnit, Inbox } from '@/types';

/**
 * Pure helpers for one inbox's business units. Shared by the Business Units table, the
 * per-unit inbox list, and the sharing dialog, so "what counts as shared" and "what changed"
 * are each decided once rather than re-derived at every call site.
 */

/**
 * The inbox's EXTRA units. The one place that treats a missing `shared_bu_ids` — an older
 * server that predates sharing — as none. Everything else, in this file and outside it,
 * should read this instead of `inbox.shared_bu_ids` directly.
 */
export const sharedUnitIds = (inbox: Inbox): number[] => inbox.shared_bu_ids ?? [];

/** Every unit the inbox belongs to: the primary first, then its extras. Skips a null primary. */
export const inboxUnitIds = (inbox: Inbox): number[] => [
  ...(inbox.bu_id != null ? [inbox.bu_id] : []),
  ...sharedUnitIds(inbox)
];

/** Two or more units is what "shared" means; one is just an ordinary assignment. */
export const isSharedInbox = (inbox: Inbox): boolean => inboxUnitIds(inbox).length >= 2;

/** Whether the inbox belongs to this unit at all — as its primary or as an extra. */
export const belongsToUnit = (inbox: Inbox, buId: number): boolean =>
  inboxUnitIds(inbox).includes(buId);

/** Whether this unit is the inbox's primary. */
export const isPrimaryUnit = (inbox: Inbox, buId: number): boolean => inbox.bu_id === buId;

/** The names of the inbox's units OTHER than `buId`, in unit order — for the "Shared with" badge. */
export const otherUnitNames = (
  inbox: Inbox,
  buId: number,
  units: readonly BusinessUnit[]
): string[] => {
  return inboxUnitIds(inbox)
    .filter((id) => id !== buId)
    .map((id) => units.find((unit) => unit.id === id)?.name)
    .filter((name): name is string => name != null);
};

/**
 * The first id in `orderedIds` that is still ticked. How the sharing dialog picks a new
 * primary once the old one is unticked — "first" meaning first in unit order, not add order.
 */
export const firstTickedId = (
  orderedIds: readonly number[],
  tickedIds: readonly number[]
): number | null => orderedIds.find((id) => tickedIds.includes(id)) ?? null;

/**
 * The order to search for a new primary once the old one is unticked: the inbox's own unit
 * order from when the dialog opened (so its existing extras keep machine's oldest-first
 * promotion order), then any unit ticked in this session that was not already on the inbox,
 * in the order it was ticked — restricted to ACTIVE units, since machine refuses an inactive
 * primary. Without this, the dialog fell back to unit-list order — the default unit first,
 * then alphabetically — which can promote a different unit than machine does when a primary
 * is removed any other way (e.g. "Unassign brand").
 */
export const primaryFallbackOrder = (
  initialUnitIds: readonly number[],
  tickedIds: readonly number[],
  activeUnitIds: readonly number[]
): number[] =>
  [...initialUnitIds, ...tickedIds.filter((id) => !initialUnitIds.includes(id))].filter((id) =>
    activeUnitIds.includes(id)
  );

/** What Save must send to turn one selection into another — see `diffBusinessUnitSelection`. */
export interface BusinessUnitSelectionDiff {
  toAdd: number[];
  toRemove: number[];
  /** Set only when the primary actually changed AND the new one is not null — mirrors what Save sends. */
  newPrimary: number | null;
}

/**
 * The minimal set of calls to turn `initial*` into `selected*`. The caller still orders
 * them — add, then promote, then remove — because a new primary must land before its
 * predecessor is removed, or the removal would trigger the server's own auto-promotion first
 * and could promote the wrong extra.
 */
export const diffBusinessUnitSelection = (
  initialUnitIds: readonly number[],
  initialPrimary: number | null,
  selectedUnitIds: readonly number[],
  selectedPrimary: number | null
): BusinessUnitSelectionDiff => {
  return {
    toAdd: selectedUnitIds.filter((id) => !initialUnitIds.includes(id)),
    toRemove: initialUnitIds.filter((id) => !selectedUnitIds.includes(id)),
    newPrimary:
      selectedPrimary != null && selectedPrimary !== initialPrimary ? selectedPrimary : null
  };
};

/** Whether a diff has anything to send — Save is disabled when this is false. */
export const hasBusinessUnitSelectionChanged = (diff: BusinessUnitSelectionDiff): boolean =>
  diff.toAdd.length > 0 || diff.toRemove.length > 0 || diff.newPrimary != null;
